import React, { useState, useEffect } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { toast } from "react-toastify";
import PaymentForm from "../../components/Form/PaymentForm";
import Header from "../../components/header/Header";
import { MenuBar } from "../../components/Menu/Menu";
import Footer from "../../components/footer/Footer";
import { getPaymentKey } from "../../store/actions/payment.action";
import { useDispatch } from "react-redux";
import PaymentHistory from "./PaymentHistory";
import VisaIcon from "../../assets/image/Visa.svg";
import Mastercard from "../../assets/image/Mastercard.svg";
import AmericanEx from "../../assets/image/AmericanEx.svg";
import WeChat from "../../assets/image/wechat-payment.svg";
import CashApp from "../../assets/image/Cashapp.svg";
import { stripe_publishable_key, CREDIT_COST } from "../../components/Transformation/Transformations";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const stripePromise = loadStripe(stripe_publishable_key);

export default function Payment() {
  const dispatch = useDispatch();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isFetch, setIsFetch] = useState(false);
  let credit_unit = 15; // Default initial credits recommendation
  const [count, setCount] = useState(15);
  const [clientSecret, setClientSecret] = useState(null);
  const { t } = useTranslation();

  const numericCount = count === "" ? 0 : parseInt(count, 10);
  const validCount = isNaN(numericCount) || numericCount < 0 ? 0 : numericCount;
  const creditCost = CREDIT_COST;
  const rawAmount = Math.round(creditCost * validCount * 100 * 1.05 + 30);
  const finalAmount = rawAmount;

  const token = localStorage.getItem("token");
  const navigation = useNavigate();
  useEffect(() => {
    if (!token) {
      navigation(`/login`);
    }
  }, [token, navigation]);

  const handleGetPaymentKey = async () => {
    if (isNaN(numericCount) || numericCount <= 0) {
      toast.error("Please enter a valid positive number of credits.", {
        position: "bottom-right",
      });
      return;
    }
    if (!finalAmount || isNaN(finalAmount) || finalAmount < 50) {
      toast.error("Total amount must be at least $0.50 USD (50 cents).", {
        position: "bottom-right",
      });
      return;
    }
    if (token) {
      setClientSecret(null);
      setIsFetch(true);
      try {
        const clientSecret = await dispatch(
          getPaymentKey({ amount: finalAmount, token })
        );
        setClientSecret(clientSecret);
      } catch (error) {
        console.error("Error fetching payment key:", error);
      }
    }
  };

  const closeMenuBar = () => {
    setMenuOpen(false);
  };

  const openMenuBar = () => {
    setMenuOpen(true);
  };

  const NumberSelector = ({ min = 1, max = 1000 }) => {
    const decrementValue = () => {
      setCount((prevValue) => {
        const num = prevValue === "" ? min : parseInt(prevValue, 10);
        const current = isNaN(num) ? min : num;
        return Math.max(min, current - 1);
      });
    };

    const handleChange = (e) => {
      const value = e.target.value;
      if (value === "") {
        setCount("");
        return;
      }
      if (/^\d+$/.test(value)) {
        const num = parseInt(value, 10);
        if (num > max) {
          setCount(max);
        } else {
          setCount(value);
        }
      }
    };

    const handleBlur = () => {
      if (count === "") {
        setCount(min);
      } else {
        const num = parseInt(count, 10);
        if (isNaN(num) || num < min) {
          setCount(min);
        }
      }
    };

    const incrementValue = () => {
      setCount((prevValue) => {
        const num = prevValue === "" ? 0 : parseInt(prevValue, 10);
        const current = isNaN(num) ? 0 : num;
        return Math.min(max, current + 1);
      });
    };

    const isMinDisabled = isFetch || (count !== "" && parseInt(count, 10) <= min);
    const isMaxDisabled = isFetch || (count !== "" && parseInt(count, 10) >= max);

    return (
      <>
        <p className="lg:pl-[5rem] font-bold md:block hidden text-xl text-gray-800">
          {t("payment_page.title")}
        </p>
        <div className="md:block flex justify-between items-center md:w-auto w-full md:mb-0 mb-3">
          <div className="flex items-center">
            {!isFetch && (
              <button
                className="text-2xl md:text-5xl lg:text-6xl 2xl:text-7xl md:w-[50px] w-8 md:h-[100px] md:mb-4 disabled:opacity-30"
                onClick={decrementValue}
                disabled={isMinDisabled}
              >
                {"-"}
              </button>
            )}
            <input
              type="text"
              className={`border-[1px] text-xl md:text-5xl lg:text-6xl 2xl:text-7xl border-[var(--brand)] bg-[var(--bg)] dm:w-[50px] w-8 sm:h-[50px] h-8 md:w-[150px] md:h-[70px] text-center ${
                isFetch ? "mx-20 mb-3" : ""
              }`}
              value={count}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isFetch}
            />
            {!isFetch && (
              <button
                className="text-2xl md:text-5xl lg:text-6xl 2xl:text-7xl md:w-[50px] w-8 md:h-[100px] md:mb-4 disabled:opacity-30"
                onClick={incrementValue}
                disabled={isMaxDisabled}
              >
                {"+"}
              </button>
            )}
          </div>
          <div className="md:hidden block text-right">
            <p className="md:mb-2 text-lg md:text-xl font-bold text-gray-800">
              {t("payment_page.total_amount")}
            </p>
            <p className="text-[var(--brand)] text-3xl md:text-5xl lg:text-6xl 2xl:text-[90px] font-bold whitespace-nowrap">
              $ {(finalAmount / 100).toFixed(2)}
            </p>
            {finalAmount < 50 && (
              <p className="text-red-500 text-xs mt-1">
                Amount must be at least $0.50 USD
              </p>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="max-w-screen-2xl mx-auto p-4 h-full">
      <Header openMenu={openMenuBar} />
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <p className="text-4xl 2xl:text-5xl font-bold md:text-center">
            {t("payment_page.title")}
          </p>
        </div>

        <div className="flex flex-col md:flex-row justify-center lg:items-center mt-4 md:mt-0 md:p-12 lg:gap-32 md:gap-16 gap-4">
          <div className="flex flex-col md:w-2/3 w-full items-center md:items-start">
            {/* Number Selector */}
            <NumberSelector
              className="w-[60px] h-[60px] md:w-[100px] md:h-[100px] text-center text-2xl md:text-4xl border-2 border-red-600 rounded-md"
              min={1}
              max={1000}
            />

            {/* Information Text */}
            <p className="text-center mt-4 sm:text-lg text-sm md:text-xl font-normal text-black">
              {t("payment_page.heading1")}{" "}
              <span className="font-bold">
                ${(creditCost * credit_unit).toFixed(2)}
              </span>{" "}
              {t("payment_page.heading2")}
            </p>
          </div>
          {/* Total Amount Section */}
          <div className="md:w-1/3 md:block hidden w-full md:text-left text-right md:mb-0 mb-3">
            <p className="mb-2 text-lg md:text-xl font-bold text-gray-800">
              {t("payment_page.total_amount")}
            </p>
            <p className="text-[var(--brand)] text-5xl md:text-5xl lg:text-6xl 2xl:text-[90px] font-bold whitespace-nowrap">
              $ {(finalAmount / 100).toFixed(2)}
            </p>
            {finalAmount < 50 && (
              <p className="text-red-500 text-sm mt-1">
                Amount must be at least $0.50 USD
              </p>
            )}
          </div>
        </div>

        <div className="justify-center">
          <div className="text-[20px] font-semibold text-center flex flex-wrap md:justify-center items-center md:gap-3">
            <div className="lg:text-xl md:text-lg text-sm">
              {t("payment_page.text1")}
            </div>
            <div className="flex items-center gap-4 overflow-x-auto whitespace-nowrap">
              <img src={VisaIcon} alt="Visa" className="h-12" />
              <img src={Mastercard} alt="Mastercard" className="h-10" />
              <img src={AmericanEx} alt="American Express" className="h-10" />
              <img src={CashApp} alt="CashApp" className="h-8" />
              <div className="text-[#2dbd07] text-[8.7px] flex items-center">
                <img src={WeChat} alt="WeChat" className="h-[35px] mr-1" />
                微信支付
              </div>
            </div>
          </div>
        </div>
        <div className="justify-center">
          <div className="text-center justify-center items-center">
            {clientSecret ? (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <PaymentForm clientSecret={clientSecret} amount={finalAmount} />
              </Elements>
            ) : (
              <button
                className="bg-blue-500 text-white px-4 py-2 rounded max-w-[500px] w-full"
                onClick={handleGetPaymentKey}
              >
                {t("payment_page.payment_button")}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2 items-center">
        <div className="text-[20px] font-semibold text-center flex flex-wrap md:justify-center items-center md:gap-3">
          <div className="lg:text-xl md:text-lg text-sm">
            {" "}
          </div>
        </div>
        <div className="flex justify-center">
          <img src="https://res.cloudinary.com/do6t8sxgo/image/upload/v1744061247/WechatIMG1245_flahrl.jpg" alt="WeChat" className="h-[280px] mr-1" />
        </div>
      </div>

      <div>
        <PaymentHistory />
      </div>
      <Footer />
      <MenuBar open={menuOpen} close={closeMenuBar} />
    </div>
  );
}
